class SlideCart extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#SlideCart-Overlay')?.addEventListener('click', this.close.bind(this));
    this.querySelectorAll('.slide-cart__close').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    const panel = this.querySelector('.slide-cart__panel');
    this.addEventListener(
      'transitionend',
      () => {
        trapFocus(panel, this.querySelector('.slide-cart__close'));
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  updateSlideCartItems(sectionHtml) {
    const sectionElement = document.getElementById('slide-cart-items');
    if (!sectionElement || !sectionHtml) return;

    const parsedHtml = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sourceContents = parsedHtml.querySelector('.js-contents');
    if (!sourceContents) return;

    const sourceItems = sourceContents.querySelector('slide-cart-items');
    const targetItems = sectionElement.querySelector('slide-cart-items');

    if (sourceItems && targetItems) {
      targetItems.innerHTML = sourceItems.innerHTML;
      return;
    }

    sectionElement.innerHTML = sourceContents.innerHTML;
  }

  updateSections(sections) {
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = sections[section.section || section.id];
      if (!sectionHtml) return;

      if (section.id === 'slide-cart-items') {
        this.updateSlideCartItems(sectionHtml);
        return;
      }

      const sectionElement = document.getElementById(section.id);
      if (!sectionElement) return;

      const parsedHtml = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const contents = parsedHtml.querySelector(section.selector || '.shopify-section');
      if (!contents) return;

      if (section.id === 'cart-upsells-slider') {
        const upsellSlider = this.querySelector('cart-upsells-slider');
        if (upsellSlider) {
          upsellSlider.innerHTML = contents.innerHTML;
        }
        return;
      }

      const targetElement =
        section.selector && sectionElement.querySelector(section.selector)
          ? sectionElement.querySelector(section.selector)
          : sectionElement;

      targetElement.innerHTML = contents.innerHTML;
    });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  refreshContents() {
    const sectionIds = this.getSectionsToRender()
      .filter((section) => section.id !== 'cart-icon-bubble' && section.id !== 'cart-upsells-slider')
      .map((section) => section.section || section.id)
      .join(',');

    return fetch(`${routes.cart_url}?sections=${sectionIds}`)
      .then((response) => response.json())
      .then((data) => {
        const sections = data?.sections || data;
        if (!sections || typeof sections !== 'object') return;
        this.updateSections(sections);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  renderContents(parsedState) {
    const preventOpen = this.preventNotification === true || this.preventNotification === 'true';

    const showDrawer = () => {
      if (preventOpen) return;
      this.classList.remove('is-empty');
      this.open();
    };

    if (parsedState?.sections) {
      this.updateSections(parsedState.sections);
    }

    return this.refreshContents().finally(showDrawer);
  }

  getSectionsToRender() {
    return [
      {
        id: 'slide-cart-header',
        section: 'slide-cart-header',
        selector: '#slide-cart-header-content',
      },
      {
        id: 'slide-cart-items',
        section: 'slide-cart-items',
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-popup-free-shipping',
        section: 'cart-popup-free-shipping',
        selector: '#cart-popup-free-shipping-content',
      },
      {
        id: 'slide-cart-footer',
        section: 'slide-cart-footer',
        selector: '#slide-cart-footer-content',
      },
      {
        id: 'cart-upsells-slider',
        section: 'cart-upsells-slider',
        selector: '#cart-upsells-slider',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('slide-cart', SlideCart);

class SlideCartItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'slide-cart-header',
        section: 'slide-cart-header',
        selector: '#slide-cart-header-content',
      },
      {
        id: 'slide-cart-items',
        section: 'slide-cart-items',
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-popup-free-shipping',
        section: 'cart-popup-free-shipping',
        selector: '#cart-popup-free-shipping-content',
      },
      {
        id: 'slide-cart-footer',
        section: 'slide-cart-footer',
        selector: '#slide-cart-footer-content',
      },
    ];
  }

  getCartStatusElement() {
    return (
      this.closest('slide-cart')?.querySelector('#slide-cart-live-region-text') ||
      document.getElementById('slide-cart-live-region-text')
    );
  }

  getLineItemStatusElement() {
    return (
      this.closest('slide-cart')?.querySelector('#slide-cart-line-item-status') ||
      document.getElementById('slide-cart-line-item-status')
    );
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=slide-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const slideCart = document.querySelector('slide-cart');
        if (slideCart) {
          slideCart.updateSlideCartItems(responseText);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        const cartItem = parsedState.items[line - 1];
        const productName = cartItem?.title || cartItem?.product_title || this.getLineItemProductName(line);

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const slideCart = document.querySelector('slide-cart');
        if (slideCart) slideCart.classList.toggle('is-empty', parsedState.item_count === 0);

        if (slideCart && parsedState?.sections) {
          slideCart.updateSections(parsedState.sections);
        }

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message, updatedValue, productName);

        const lineItem = document.getElementById(`CartItem-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`) && slideCart) {
          trapFocus(slideCart.querySelector('.slide-cart__panel'), lineItem.querySelector(`[name="${name}"]`));
        } else if (parsedState.item_count === 0 && slideCart) {
          trapFocus(slideCart.querySelector('.slide-cart__panel'), slideCart.querySelector('.slide-cart__close'));
        } else if (document.querySelector('.cart-item') && slideCart) {
          trapFocus(slideCart.querySelector('.slide-cart__panel'), document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'slide-cart-items' });
      })
      .catch((e) => {
        console.error(e);
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('slide-cart-errors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

}

customElements.define('slide-cart-items', SlideCartItems);
